import { FingerPrint } from '../fingerPrint';

import { getActiveToken, getActiveUser } from './auth';
import { StreamCommunication } from './streamCommunication';
import { Lange } from './testConfiguration';

export type SurveyInformation = {
  surveyId: string;
  surveyNumber: string;
  testName: string;
  taskNumber: string;
  startDate: Date | null;
  elapsedSeconds: number;
  estimatedDurationSeconds: number;
  lastActivityAt: string | null;
  fingerPrint: string;
  lange: Lange;
  mode: 'Standard' | 'Public';
  userDisplayName: string;
  userEmail: string;
  userRoles: string[];
  supervisors: SupervisorInfo[];
};

type SupervisorInfo = {
  name: string;
  role: string;
};

export class SupervisorMonitor {
  protected streamCommunication: StreamCommunication | null = null;
  protected surveyInformation: SurveyInformation;

  constructor() {
    this.surveyInformation = {
      surveyId: '',
      surveyNumber: '',
      testName: '',
      taskNumber: '',
      startDate: null,
      elapsedSeconds: 0,
      estimatedDurationSeconds: 0,
      lastActivityAt: null,
      fingerPrint: FingerPrint.getRawFingerPrint(),
      lange: Lange.PL,
      mode: 'Standard',
      userDisplayName: '',
      userEmail: '',
      userRoles: [],
      supervisors: [],
    };
  }

  public setTestInformation(testName: string, taskNumber: string) {
    this.surveyInformation.testName = testName;
    this.surveyInformation.taskNumber = taskNumber;
  }

  public setLange(lange: Lange) {
    this.surveyInformation.lange = lange;
  }

  public setPublicMode() {
    this.surveyInformation.mode = 'Public';
  }

  public setStandardMode() {
    this.surveyInformation.mode = 'Standard';
  }

  public setSupervisors(supervisors: SupervisorInfo[]) {
    this.surveyInformation.supervisors = supervisors;
  }

  public setEstimatedDurationSeconds(seconds: number) {
    this.surveyInformation.estimatedDurationSeconds = seconds;
  }

  public updateElapsedSeconds() {
    if (!this.surveyInformation.startDate) {
      this.surveyInformation.elapsedSeconds = 0;
      return;
    }
    const diffMs =
      new Date().getTime() - this.surveyInformation.startDate.getTime();
    this.surveyInformation.elapsedSeconds = Math.max(
      0,
      Math.floor(diffMs / 1000),
    );
  }

  public markActivity() {
    this.surveyInformation.lastActivityAt = new Date().toISOString();
  }

  public async setUser() {
    const user = await getActiveUser();
    if (user) {
      this.surveyInformation.userDisplayName = user.displayName || '';
      this.surveyInformation.userEmail = user.email || '';
      this.surveyInformation.userRoles = user.roles || [];
    }
  }

  public sendSurveyInformation() {
    this.updateElapsedSeconds();
    void this.streamCommunication?.sendSurveyInformation(
      this.surveyInformation.surveyId,
      this.surveyInformation,
    );
  }

  public async activateCommunication(
    surveyId: string,
    surveyNumber: string,
    startStreaming = false,
  ) {
    this.surveyInformation.surveyId = surveyId;
    this.surveyInformation.surveyNumber = surveyNumber;
    this.surveyInformation.startDate = new Date();
    await this.setUser();
    if (this.streamCommunication) {
      this.streamCommunication.destroy();
      this.streamCommunication = null;
    }
    this.streamCommunication = new StreamCommunication(await getActiveToken());
    this.sendSurveyInformation();
    startStreaming && this.streamCommunication.startStreaming(surveyId);
  }

  public stopStreaming() {
    if (this.streamCommunication) {
      this.streamCommunication.destroy();
      this.streamCommunication = null;
    }
  }

  public getSurveyInformation(): SurveyInformation {
    return this.surveyInformation;
  }

  public async sendCallForHelp() {
    await this.streamCommunication?.sendCallForHelp(
      this.surveyInformation.surveyId,
    );
  }
}
